import { Container, Graphics, Sprite, Text, type Texture } from 'pixi.js';
import type { HiLoOffer, HiLoState } from '@goodtime/shared';
import { AudioBus } from '../../audio/AudioBus';
import { hiloValue } from '../../logic/road/hilo';
import { ROAD_SOUNDS } from '../../logic/road/sounds';
import { BoardDialog } from '../board/BoardDialog';
import { BrassButton, layoutButtonRow } from '../board/BrassButton';
import { woodGradient } from '../board/BoardPanel';
import { animate, COLOURS, DISPLAY_FONT, displayStyle, ease, uiStyle } from '../board/ui';

/** The climb, as the server sets it (base x each rung). */
const LADDER = [1, 1.25, 1.6, 2, 2.5, 3.2];

/** Hi-Lo as a friend's game rather than the bonus after a run: Willow's
 *  lamp in a run (paying the run's pot) or her free first game. */
export interface HiLoMode {
  /** The plaque ("ACORN HI-LO"); the bonus's by default. */
  title?: string;
  /** The line under the offer. */
  sub?: string;
  /** The payout went into the run's pot: no coins fly to the wallet. */
  intoPot?: boolean;
}

/** What the dialog needs from the host: the server calls. */
export interface HiLoApi {
  act(step: number, action: 'start' | 'guess' | 'take', arg?: 'higher' | 'lower'): Promise<{ step: number; state: HiLoState; over: boolean; payout: number | null }>;
  decline(): Promise<void>;
  /** After a failed call: where the play really is, or null if unknown. */
  recover(): Promise<{ step: number; state: HiLoState | null; finished: boolean; payout: number | null } | null>;
  beacon?(event: 'hilo_offered' | 'hilo_taken'): void;
}

/**
 * The Hi-Lo bonus after a collected Wheel Run, drawn in Pixi on a board.
 * The run pot is already in the wallet; this plays a separate base. Guess
 * right (a tie counts) to climb; a wrong guess ends it and pays the base,
 * and says so plainly first (producer 2026-10-14: "it wasn't obvious I'd
 * lost"). Buttons: cancel left, action right. No odds are shown.
 */
export class HiLoDialog extends BoardDialog {
  private readonly playingCard = new Container();
  private readonly cardFace = new Graphics();
  private readonly cardFrame = new Sprite();
  private readonly rank: Text;
  private readonly ladder = new Container();
  private readonly line: Text;
  private readonly sub: Text;
  private readonly result: Text;
  private readonly left: BrassButton;
  private readonly right: BrassButton;
  private readonly take: BrassButton;
  private offer: HiLoOffer | null = null;
  private api: HiLoApi | null = null;
  private state: HiLoState | null = null;
  private step = 0;
  private busy = false;
  private done: ((payout: number | null) => void) | null = null;
  private phase: 'offer' | 'play' | 'over' = 'offer';
  private payout: number | null = null;
  private lost = false;
  private mode: HiLoMode = {};

  /** `pay` puts the payout in the wallet: flying coins for a win, quietly
   *  for the base kept after a wrong guess. */
  constructor(
    private readonly art: (name: 'card' | 'floor') => Texture | null,
    private readonly pay: (from: { x: number; y: number }, amount: number, fly: boolean) => Promise<void>,
  ) {
    super({ title: 'HI-LO BONUS', floor: art('floor') });
    this.cardFrame.anchor.set(0.5);
    this.rank = new Text({ text: '?', style: { fontFamily: DISPLAY_FONT, fontSize: 58, fill: 0x3a1c05 } });
    this.rank.anchor.set(0.5);
    this.playingCard.addChild(this.cardFace, this.cardFrame, this.rank);
    this.line = new Text({ text: '', style: { ...uiStyle(17), align: 'center', wordWrap: true } });
    this.line.anchor.set(0.5, 0);
    this.sub = new Text({ text: '', style: { ...uiStyle(14, 0xf0dcb4, '500'), align: 'center', wordWrap: true } });
    this.sub.anchor.set(0.5, 0);
    this.result = new Text({ text: '', style: displayStyle(26) });
    this.result.anchor.set(0.5, 0);
    this.left = new BrassButton({ label: 'No thanks', kind: 'wood', size: 16, onTap: () => void this.onLeft() });
    this.right = new BrassButton({ label: 'PLAY', kind: 'gold', onTap: () => void this.onRight() });
    this.take = new BrassButton({ label: 'TAKE', kind: 'gold', onTap: () => void this.act('take') });
    this.content.addChild(this.playingCard, this.ladder, this.line, this.sub, this.result, this.left, this.right, this.take);
  }

  /** Offer and play the bonus; resolves with the payout (its coins have
   *  flown into the wallet) or null if declined. */
  play(offer: HiLoOffer, api: HiLoApi, mode: HiLoMode = {}): Promise<number | null> {
    this.mode = mode;
    this.setTitle(mode.title ?? 'HI-LO BONUS');
    this.offer = offer;
    this.api = api;
    this.state = null;
    this.step = 0;
    this.payout = null;
    this.lost = false;
    this.phase = 'offer';
    this.setFloor(this.art('floor'));
    api.beacon?.('hilo_offered');
    this.render();
    void this.open();
    return new Promise((resolve) => (this.done = resolve));
  }

  /** What the dialog shows, for tests. */
  get snapshot(): { open: boolean; card: string; line: string; sub: string; result: string; buttons: string[] } {
    const buttons = [this.left, this.right, this.take].filter((b) => b.visible).map((b) => b.labelText);
    return {
      open: this.visible,
      card: this.rank.text,
      line: this.line.text,
      sub: this.sub.text,
      result: this.result.visible ? this.result.text : '',
      buttons,
    };
  }

  /** Press the visible button whose label matches, as a tap would (tests). */
  press(label: string): boolean {
    const button = [this.left, this.right, this.take].find((b) => b.visible && b.labelText.startsWith(label));
    button?.onTap?.();
    return !!button;
  }

  private async onLeft(): Promise<void> {
    if (this.busy || !this.api) return;
    if (this.phase === 'offer') {
      this.busy = true;
      await this.api.decline().catch(() => undefined);
      await this.finish(null);
    } else if (this.phase === 'play') {
      await this.act('guess', 'higher');
    }
  }

  private async onRight(): Promise<void> {
    if (this.busy) return;
    if (this.phase === 'offer') {
      this.api?.beacon?.('hilo_taken');
      await this.act('start');
    } else if (this.phase === 'play') {
      await this.act('guess', 'lower');
    } else {
      // Collect: a win's coins fly to the wallet, then the dialog closes.
      this.busy = true;
      const at = this.result.getGlobalPosition();
      // A lamp's game paid the run's pot, not the wallet.
      if (!this.mode.intoPot) await this.pay(at, this.payout ?? 0, !this.lost);
      await this.finish(this.payout);
    }
  }

  private async act(action: 'start' | 'guess' | 'take', arg?: 'higher' | 'lower'): Promise<void> {
    if (!this.api || this.busy) return;
    this.busy = true;
    const before = this.state;
    try {
      const res = await this.api.act(this.step, action, arg);
      this.step = res.step + 1;
      this.state = res.state;
      if (action !== 'take') AudioBus.shared.play(ROAD_SOUNDS.hiloFlip, { volume: 0.7 });
      if (res.over) {
        this.payout = res.payout ?? 0;
        this.phase = 'over';
        const lost = action === 'guess' && this.payout <= (before?.floor ?? this.offer?.base ?? 0);
        this.lost = lost;
        const after = action === 'take' ? ROAD_SOUNDS.coinBurst : lost ? ROAD_SOUNDS.hiloEnd : ROAD_SOUNDS.collected;
        setTimeout(() => AudioBus.shared.play(after, { volume: 0.8 }), 220);
        this.render(lost ? 'lost' : 'won');
        // A win gets confetti from the amount won.
        if (!lost) void this.celebrate({ x: this.contentWidth / 2, y: this.result.y + this.result.height / 2 });
      } else {
        this.phase = 'play';
        if (action === 'guess') setTimeout(() => AudioBus.shared.play(ROAD_SOUNDS.hiloRungUp, { volume: 0.8 }), 220);
        this.render(action === 'guess' ? 'right' : undefined);
      }
      if (action !== 'take') void this.flipCard();
    } catch {
      const play = await this.api.recover().catch(() => null);
      if (play) {
        this.step = play.step;
        if (play.state) this.state = play.state;
        if (play.finished) {
          this.payout = play.payout ?? 0;
          this.phase = 'over';
        }
        this.render();
      } else {
        this.sub.text = 'Lost the connection. Try again.';
        this.sub.style.fill = COLOURS.loss;
      }
    } finally {
      this.busy = false;
    }
  }

  /** Lay the dialog out for its phase. `moment` colours the line. */
  private render(moment?: 'right' | 'won' | 'lost'): void {
    const w = this.contentWidth;
    const offer = this.offer;
    const s = this.state;
    if (!offer) return;
    let y = 0;

    // The card.
    const cardW = 92;
    const cardH = 127;
    const frame = this.art('card');
    if (frame) {
      this.cardFrame.texture = frame;
      // The frame art is a square with the card at x 194-829, y 61-938 of
      // 1024: at 148 px that card is 92 x 127, centred 2 px above the art's.
      this.cardFrame.width = this.cardFrame.height = 148;
      this.cardFrame.position.set(0, 2);
    }
    this.cardFace.clear().roundRect(-cardW / 2 + 4, -cardH / 2 + 4, cardW - 8, cardH - 8, 10).fill({ color: 0xf6ead0 });
    this.rank.text = s ? String(s.card) : '?';
    this.playingCard.position.set(w / 2, cardH / 2 + 2);
    y = cardH + 14;

    // The ladder (not on the offer).
    this.ladder.removeChildren().forEach((c) => c.destroy({ children: true }));
    this.ladder.visible = this.phase !== 'offer';
    if (this.phase !== 'offer') {
      const chips = LADDER.map((_, rung) => {
        const value = hiloValue(offer.base, LADDER, rung);
        const on = s ? rung === s.rung : rung === 0;
        const t = new Text({ text: String(value), style: { fontFamily: DISPLAY_FONT, fontSize: 14, fill: on ? 0x3a1a00 : 0xffe8b0 } });
        const chip = new Container();
        const g = new Graphics()
          .roundRect(0, 0, t.width + 14, 24, 8)
          .fill(on ? woodGradient(0xffe08a, 0xe0a020) : { color: 0x1e1206, alpha: 0.7 })
          .stroke({ color: 0xd9a441, alpha: 0.6, width: 1 });
        t.position.set(7, 3);
        chip.addChild(g, t);
        return chip;
      });
      const total = chips.reduce((n, c) => n + c.width, 0) + 5 * (chips.length - 1);
      let x = (w - total) / 2;
      chips.forEach((c) => {
        c.position.set(x, 0);
        x += c.width + 5;
        this.ladder.addChild(c);
      });
      this.ladder.y = y;
      y += 34;
    }

    // Words.
    this.line.style.wordWrapWidth = w;
    this.sub.style.wordWrapWidth = w;
    this.sub.style.fill = 0xf0dcb4;
    this.result.visible = false;
    if (this.phase === 'offer') {
      this.line.text = `Play for ${offer.base} coins, up to ${offer.maxValue}.`;
      this.sub.text = this.mode.sub ?? 'Your run pot is already in your wallet.';
    } else if (this.phase === 'play' && s) {
      this.line.text = moment === 'right' ? 'Right! Up a rung.' : 'Higher or lower than the next card?';
      this.line.style.fill = moment === 'right' ? 0x9be89b : COLOURS.cream;
      this.sub.text = `A tie counts. A wrong guess ends it; you keep ${s.floor}.`;
    } else {
      const lost = moment === 'lost';
      this.line.text = lost ? `Wrong! It was ${s?.card ?? '?'}. The climb ends here.` : moment === 'won' ? 'You won!' : 'All done.';
      this.line.style.fill = lost ? COLOURS.loss : COLOURS.cream;
      this.sub.text = lost ? `You keep the ${this.payout} you started with.` : '';
      this.result.text = lost ? `${this.payout} coins` : `+${this.payout} coins`;
      this.result.style = displayStyle(lost ? 22 : 28, lost ? 0xe8d8b8 : COLOURS.gold);
      this.result.visible = true;
    }
    this.line.position.set(w / 2, y);
    y += this.line.height + 4;
    if (this.sub.text) {
      this.sub.position.set(w / 2, y);
      y += this.sub.height + 6;
    }
    if (this.result.visible) {
      this.result.position.set(w / 2, y);
      y += this.result.height + 8;
    }
    y += 6;

    // Buttons: cancel or the second choice on the left, the action on the right.
    this.take.visible = false;
    if (this.phase === 'offer') {
      this.left.setLabel('No thanks');
      this.right.setLabel('PLAY');
      this.left.visible = this.right.visible = true;
      layoutButtonRow(this.left, this.right, 0, y, w);
      y += 52;
    } else if (this.phase === 'play' && s) {
      this.left.setLabel('▲ HIGHER');
      this.right.setLabel('▼ LOWER');
      this.left.visible = this.right.visible = true;
      layoutButtonRow(this.left, this.right, 0, y, w);
      y += 58;
      this.take.setLabel(`TAKE ${s.value}`);
      this.take.visible = true;
      layoutButtonRow(null, this.take, 0, y, w);
      y += 52;
    } else {
      this.left.visible = false;
      this.right.setLabel(this.lost ? 'DONE' : `COLLECT ${this.payout ?? 0}`);
      this.right.visible = true;
      layoutButtonRow(null, this.right, 0, y, w);
      y += 52;
    }
    this.setContentHeight(y);
  }

  private async flipCard(): Promise<void> {
    await animate(260, (t) => {
      const k = Math.abs(Math.cos(t * Math.PI));
      this.playingCard.scale.x = Math.max(0.05, ease.outCubic(k));
    });
    this.playingCard.scale.x = 1;
  }

  private async finish(payout: number | null): Promise<void> {
    await this.close();
    this.busy = false;
    const done = this.done;
    this.done = null;
    done?.(payout);
  }
}
